// App.tsx — Root: AuthProvider wraps everything; the HashRouter
// owns route content. The auth status FSM picks which top-level
// surface renders (splash / gate / mfa / shell / denied).

import { useEffect } from 'react';
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/services/auth';
import { useAuth } from '@/services/useAuth';
import { useLegacyUrlRewrite } from '@/hooks/useLegacyUrlRewrite';
import { AppShell } from '@/components/AppShell';
import { AuthGate } from '@/components/AuthGate';
import { MfaGate } from '@/components/MfaGate';
import { LoadingSplash } from '@/components/LoadingSplash';
import { bump } from '@/utils/bootCounters';

import { LicenseOrgsPage } from '@/routes/LicenseOrgsPage';
import { LicenseOrgDetailPage } from '@/routes/LicenseOrgDetailPage';
import { MembersPage } from '@/routes/MembersPage';
import { InvitesPage } from '@/routes/InvitesPage';
import { AuditPage } from '@/routes/AuditPage';
import { SettingsPage } from '@/routes/SettingsPage';
import { UnlockCodesPage } from '@/routes/UnlockCodesPage';
import { UserProfileDrawerProvider } from '@/components/UserProfileDrawer';

function AuthGatedShell() {
  const { status, errorMessage } = useAuth();
  useLegacyUrlRewrite();

  // RecaptchaVerifier needs an in-DOM host element AT THE MOMENT
  // beginMfa() runs - which is during the 'gate' phase, before
  // MfaGate has mounted. Keep the host permanently rendered at the
  // root so the FSM transition never beats it to the DOM.
  const recaptchaHost = <div id="recaptcha-container" />;

  if (status === 'loading') {
    return (
      <>
        <LoadingSplash />
        {recaptchaHost}
      </>
    );
  }
  if (status === 'gate' || status === 'denied') {
    return (
      <>
        <AuthGate />
        {recaptchaHost}
      </>
    );
  }
  if (status === 'mfa') {
    return (
      <>
        <MfaGate />
        {recaptchaHost}
      </>
    );
  }

  // status === 'ready'
  return (
    <UserProfileDrawerProvider>
      <AppShell>
        {errorMessage && (
          <div className="console-form-error" role="alert">
            {errorMessage}
          </div>
        )}
        <Routes>
          <Route index element={<Navigate to="/license-orgs" replace />} />
          <Route path="/license-orgs" element={<LicenseOrgsPage />} />
          <Route
            path="/license-orgs/:orgId"
            element={<LicenseOrgDetailPage />}
          />
          <Route
            path="/license-orgs/:orgId/members"
            element={<MembersPage />}
          />
          <Route
            path="/license-orgs/:orgId/invites"
            element={<InvitesPage />}
          />
          <Route
            path="/license-orgs/:orgId/audit"
            element={<AuditPage />}
          />
          <Route path="/unlock-codes" element={<UnlockCodesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/license-orgs" replace />} />
        </Routes>
        {recaptchaHost}
      </AppShell>
    </UserProfileDrawerProvider>
  );
}

export function App() {
  useEffect(() => {
    bump('routerStart');
  }, []);
  return (
    <HashRouter>
      <AuthProvider>
        <AuthGatedShell />
      </AuthProvider>
    </HashRouter>
  );
}
